import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { track } from 'analytics';
import { connectToBrand_api, disconnectAccounts_api } from 'api/brandApi';
import { checkIfUrlExpired_api } from 'api/generalApi';
import FB, { FB_AD_ACCOUNT_STATUS, checkForFBPermissions, FacebookService, getFBAuthResponse, loginWithFB, queryFB } from '../helpers/FB';
import { FacebookPageType, InstagramPageType, LoggedUser, FacebookBusinessManager, AdAccountPageType, FBPixel, PixelConversionEvent, CurrentBrand } from 'modules/Types';
import { getFacebookImage_api, getInstagramAccountFromFbPage_api, isValidAccessToken_api, loggedInFb_api, getFacebookPages_api, getAdAccounts_api, getFacebookPixelsForAdAccount, getConversionEventsForPixel } from 'api/facebookApi';
import { getPreconnectionConfigurationsStatus, getAdAccountDetails, PreConnectionConfigs, getMinimalAmount } from '../pages/MarketingPage/utils/fbCampaigns';
import { CSSProperties } from 'react';
import Colors from 'modules/Colors';
import ButtonSymphony from 'elements/ButtonSymphony';
import useBrand, { BrandExistsState } from './useBrand';
import Logger from 'Logger';
import {
  CREDIT_CARD,
  DEFAULT_CURRENCY,
  PAYMENT_METHODS,
  PAYPAL_BILLING_AGREEMENT
} from 'modules/Const';
import { getAccountStatus, getTosConfiguration, getTosStatus } from 'pages/MarketingPage/utils';
import { getTosStatusApi } from 'pages/MarketingPage/api';
import { MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS } from 'modules/Const';

const ERROR_STATES = {
  PERMISSIONS_ERROR: {
    message: 'We can\'t load your Facebook Pages because we don\'t have permission. Click the button below to authorize access to your pages.'
  },
  NO_PAGES_ERROR: {
    message: 'It looks like you don\'t have any Facebook pages associated with your account. Please create a new Facebook page by following the instructions at the link.'
  }
} as const;

/** Helper function to update user profile with fresh data if needed */
const getRefreshedFbUser = async (currentUser: LoggedUser) => {
  const isExpiredPicture = await checkIfUrlExpired_api(currentUser.picture?.data.url || '');
  const needsProfileData = !currentUser.id || !currentUser.name || !currentUser.picture || isExpiredPicture;

  if (needsProfileData) {
    try {
      const profileData = await loggedInFb_api();
      return profileData
    } catch (error) {
      console.error('Error refreshing Facebook profile:', error);
      return currentUser;
    }
  } else {
    return currentUser;
  }
}

/** 
 * Validates and refreshes Facebook and Instagram profile images if they're expired
 * @param fbPage - Facebook page data
 * @param igPage - Instagram page data
 * @param accessToken - Facebook access token
 * @returns Updated Facebook and Instagram page data
 */
const refreshSocialImages = async (
  fbPage: FacebookPageType | null,
  igPage: InstagramPageType | null,
  accessToken?: string
): Promise<{
  fbPage: FacebookPageType | null,
  igPage: InstagramPageType | null
}> => {
  if (!fbPage) return { fbPage: null, igPage: null }

  let updatedFbPage = fbPage
  let updatedIgPage = igPage

  try {
    // Check and update Facebook image
    if (fbPage?.picture?.data?.url) {
      const isFbImageExpired = await checkIfUrlExpired_api(fbPage.picture.data.url)
      if (isFbImageExpired) {
        const fbImage = await getFacebookImage_api(fbPage.id, accessToken)
        updatedFbPage = {
          ...fbPage,
          picture: { data: { url: fbImage } }
        } as FacebookPageType
      }
    }

    // Check and update Instagram image only if we have a Facebook page
    if (igPage?.instagramAccount?.profile_pic) {
      const isIgImageExpired = await checkIfUrlExpired_api(igPage.instagramAccount.profile_pic)
      if (isIgImageExpired) {
        updatedIgPage = await getInstagramAccountFromFbPage_api(updatedFbPage, accessToken)
      }
    }

    return { fbPage: updatedFbPage, igPage: updatedIgPage || igPage }
  } catch (error) {
    Logger.error('Error refreshing social images:', error)
    return { fbPage, igPage }
  }
}

/** Types of terms of service that can be checked */
export type TermsType = 'all' | 'custom' | 'valueBased' | 'userTos';

interface PreConnectionStatus {
  /** Overall status of all terms acceptance */
  allTermsAccepted: boolean;
  /** Status of custom audience terms */
  customAudienceAccepted: boolean;
  /** Status of value-based audience terms */
  valueBasedAudienceAccepted: boolean;
  /** Status of user terms */
  userTosAccepted: boolean;
}


interface PaymentMethodType {
  display_string: string;
  id: string;
  type: string;
  spendCap?: number;
  balance?: number;
  currency?: string;
  minimumCurrencyBudgetPerDay?: number;
}

/** Interface for Facebook connection state */
interface FacebookConnectionState {
  /** State */
  isLoadingFacebookPages: boolean;
  isCheckingPermissions: boolean;
  isConnecting: boolean;

  /** Loading states */
  isLoadingFacebookAdAccounts: boolean;
  isLoadingFacebookPixels: boolean;
  isLoadingFacebookConversionEvents: boolean;
  isLoadingPaymentMethod: boolean;
  isLoggingIntoFacebook: boolean;
  validatingConnections: boolean;

  /** Error states */
  permissionsError: keyof typeof ERROR_STATES | null;

  /** Facebook pages and accounts */
  facebookPages: FacebookPageType[];
  facebookBusinessManager: FacebookBusinessManager | null;
  loggedInFacebookUser: LoggedUser | null;
  hasInstagramPagesAvailable: boolean;

  /** Selected connections */
  selectedFacebookPage: FacebookPageType | null;
  selectedInstagramPage: InstagramPageType | null;
  selectedFacebookAdAccount: AdAccountPageType | null;
  selectedFacebookPixel: FBPixel | null;

  /** Terms and permissions */
  canRunAds: boolean;
  termsOfServiceAccepted: boolean;
  adsTermsOfServiceStatus: PreConnectionConfigs | undefined;

  /** Payment info */
  adAccountPaymentMethod: PaymentMethodType | null;

  /** Methods */
  getConversionEvents: () => Promise<PixelConversionEvent[]>;
  getFacebookPixels: () => Promise<FBPixel[]>;
  setFacebookPixel: (pixel: FBPixel | null) => void;
  loadPaymentDetails: (fbAdAccountId: string) => Promise<PaymentMethodType | null>;
  setAdsTermsOfServiceStatus: (status: PreConnectionConfigs | undefined) => void;
  loginWithFacebook: () => Promise<LoggedUser | null>;
  refreshIgConnection: (autoconnectToBrand?: boolean) => Promise<InstagramPageType | null>;
  reRequestPermissions: () => Promise<void>;
  getFacebookPages: (includeInstagram?: boolean) => Promise<FacebookPageType[] | []>;
  getFacebookAdAccounts: (businessManagerId?: string) => Promise<AdAccountPageType[]>;
  shouldShowLoginButton: () => boolean;
  renderFacebookLoginButton: () => JSX.Element | null;
  renderFacebookErrorMessage: () => JSX.Element | null;
  logoutFacebook: () => Promise<void>;
  /** Gets or refreshes the Facebook auth token */
  getFacebookAuthToken: () => Promise<string | null>;
  /** Gets fresh login status from Facebook */
  // getLoginStatus: () => Promise<boolean>;
  setFacebookPage: (page: FacebookPageType | null) => Promise<CurrentBrand | null>;
  setInstagramPage: (page: InstagramPageType | null) => void;
  setFacebookAdAccount: (adAccount: AdAccountPageType | null) => Promise<CurrentBrand | null>;
  initializeFB: (brand?: CurrentBrand) => Promise<boolean>;
  removeConnections: (removeLoggedInUser?: boolean) => void;
  igIsBusinessAccount: () => boolean;
  checkIfReadyToRunAds: (args?: {
    facebookAdAccount?: AdAccountPageType | null,
    requiresIgConnection?: boolean,
    expressSetupOrCustomPages?: 'custom' | 'express'
  }) => Promise<boolean>;
  getTermsAcceptanceStatus: (adAccount: AdAccountPageType) => Promise<PreConnectionStatus | null>;
  hasUserAcceptedAdsTerms: (adAccount: AdAccountPageType) => Promise<boolean>;
}

/**
 * Connects a Facebook user to a brand by storing their access token
 * @param args Object containing connection parameters
 * @param args.brandId ID of the brand to connect to
 * @param args.brandSlug URL slug of the brand
 * @param args.accessToken Facebook access token to store
 */
export const connectUserToFacebook = async (args: {
  brandId: number,
  brandSlug: string,
  accessToken: string
}) => {
  const { brandId, brandSlug, accessToken } = args
  let logged_in_fb_user = accessToken;
  if (logged_in_fb_user) {
    await connectToBrand_api({
      brandId,
      brandSlug,
      service: 'logged_in_fb_user',
      value: {
        logged_in_fb_user,
      }
    })
  }
}

export const useFacebookConnections = create(
  immer<FacebookConnectionState>((set, get) => ({
    // Initial state
    isLoadingFacebookPages: false,
    isCheckingPermissions: false,
    isConnecting: false,
    isLoggingIntoFacebook: false,
    needsToReLogin: false,
    permissionsError: null,
    facebookPages: [],
    selectedFacebookPage: null,
    selectedInstagramPage: null,
    selectedFacebookAdAccount: null,
    isLoadingFacebookAdAccounts: false,
    selectedFacebookPixel: null,
    hasInstagramPagesAvailable: false,
    facebookBusinessManager: null,
    loggedInFacebookUser: null,
    searchQuery: '',
    conversionsEventOptions: [],
    conversionsEventName: undefined,
    preConnectionConfigurations: undefined,
    areTermsAccepted: false,
    connectionsType: '',
    confirmConnections: false,
    conversionsEnabled: false,
    validatingConnections: false,
    areConnectionsValid: false,
    canRunAds: false,
    adAccountPaymentMethod: null,
    isLoadingPaymentMethod: false,
    isLoadingFacebookConversionEvents: false,
    isLoadingFacebookPixels: false,
    facebookPixels: [],
    adsTermsOfServiceStatus: undefined,
    termsOfServiceAccepted: false,



    getConversionEvents: async (): Promise<PixelConversionEvent[]> => {
      const { selectedFacebookPixel } = get();
      if (!selectedFacebookPixel) return [];

      try {
        set((state) => {
          state.isLoadingFacebookConversionEvents = true;
        });

        const events: {
          start_time: string;
          aggregation: string;
          data: { value: string; count: number; }[];
        }[] = await getConversionEventsForPixel(selectedFacebookPixel);
        // Create a map to store aggregated counts
        const eventMap = new Map<string, number>();

        // Aggregate counts across all time periods
        (events as {
          start_time: string;
          aggregation: string;
          data: { value: string; count: number; }[];
        }[]).forEach((timeSlot) => {
          timeSlot.data.forEach((event) => {
            const currentCount = eventMap.get(event.value) || 0;
            eventMap.set(event.value, currentCount + event.count);
          });
        });
        // Convert map to array and sort by count descending
        return Array.from(eventMap.entries())
          .map(([value, count]) => ({
            name: value,
            value,
            count,
            time: new Date().toISOString(),
            type: 'custom' as const
          }))
          .sort((a, b) => b.count - a.count);

      } catch (error) {
        console.error('Error getting conversion events:', error);
        return [];
      } finally {
        set((state) => {
          state.isLoadingFacebookConversionEvents = false;
        });
      }
    },

    getFacebookPixels: async (): Promise<FBPixel[]> => {
      const { currentBrand } = useBrand.getState() as BrandExistsState;
      try {
        set((state) => {
          state.isLoadingFacebookPixels = true
        })
        const {
          selectedFacebookAdAccount
        } = get()

        if (!selectedFacebookAdAccount) return [];

        const pixels = await getFacebookPixelsForAdAccount(selectedFacebookAdAccount)
        return pixels;
      } catch (error) {
        console.error('Error getting Facebook pixels:', error);
        return [];
      } finally {
        set((state) => {
          state.isLoadingFacebookPixels = false
        })
      }
    },
    setFacebookPixel: async (pixel: FBPixel | null) => {
      const { currentBrand, setCurrentBrand } = useBrand.getState() as BrandExistsState;

      try {
        if (!pixel) {
          set((state) => {
            state.selectedFacebookPixel = null
          });
          return null;
        }

        set((state) => {
          state.isConnecting = true;
          state.selectedFacebookPixel = pixel;
        });

        // Connect ad account to brand
        const updatedBrand = await connectToBrand_api({
          brandSlug: currentBrand?.slug,
          brandId: currentBrand?.id,
          service: 'facebook_pixel',
          value: pixel
        });

        // Update current brand with new ad account
        setCurrentBrand(updatedBrand);
        return updatedBrand;
      } catch (error) {
        console.error('Error connecting Facebook pixel:', error);

        // Reset state on error
        set((state) => {
          state.selectedFacebookPixel = null
        });

        throw error;
      } finally {
        set((state) => {
          state.isConnecting = false
        });

        return null
      }
    },
    refreshIgConnection: async (): Promise<InstagramPageType | null> => {
      // const { connectSelectedFacebookInstagramPages: connectSelectedFacebookInstagramPages } = get();

      // try {

      //   // If autoconnectToBrand is true, connect both accounts to brand
      //   const { currentBrand } = useBrand.getState() as BrandExistsState;

      //   const brandDetails = await connectSelectedFacebookInstagramPages({
      //     autoConnectInstagram: true,
      //   })

      //   const igPage = brandDetails?.connections?.instagram_page;
      //   if (!igPage) return null;
      //   return igPage;
      // } catch (error) {
      //   console.error('Error refreshing Instagram connection:', error);
      //   track('Error refreshing Instagram connection', { error });
      //   set((state) => ({ instagramPagesAvailable: false })); // Set to false on error
      return null;
      // }
    },

    setFacebookPage: async (page: FacebookPageType | null): Promise<CurrentBrand | null> => {
      const { getFacebookAuthToken, loggedInFacebookUser: loggedInUser } = get();

      const { currentBrand, setCurrentBrand } = useBrand.getState() as BrandExistsState;

      try {
        if (!page) {
          set(state => {
            state.selectedFacebookPage = null;
            state.selectedInstagramPage = null;
            state.hasInstagramPagesAvailable = false;
          });
          return null;
        }

        set(state => {
          state.isConnecting = true;
          state.selectedFacebookPage = page;
        });

        const accessToken = loggedInUser?.access_token || await getFacebookAuthToken();

        // Connect Facebook page to brand
        let updatedBrand = await connectToBrand_api({
          brandId: currentBrand?.id,
          brandSlug: currentBrand?.slug,
          service: 'facebook_page',
          value: {
            facebook_page: page,
            logged_in_fb_user: accessToken
          }
        });

        // Handle Instagram auto-connect if requested

        try {
          const igAccount = await getInstagramAccountFromFbPage_api(page, page?.access_token ?? undefined);

          if (igAccount) {
            updatedBrand = await connectToBrand_api({
              brandId: currentBrand?.id,
              brandSlug: currentBrand?.slug,
              service: 'instagram_page',
              value: {
                businessAccount: igAccount.businessAccount,
                instagramAccount: igAccount.instagramAccount,
                connected_instagram_account: igAccount.connected_instagram_account
              }
            });

            set((state) => {
              state.selectedInstagramPage = igAccount;
              state.hasInstagramPagesAvailable = true;
            });
          } else {
            set((state) => {
              state.selectedInstagramPage = null;
              state.hasInstagramPagesAvailable = false;
            });
          }
        } catch (error) {
          console.error('Error connecting Instagram account:', error);
          track('Error connecting Instagram account', { error });
        }


        // set current brand with the updated FB / IG pages
        setCurrentBrand(updatedBrand);
        return updatedBrand;
      } catch (error) {
        console.error('Error connecting Facebook page:', error);
        // Reset state on error
        set(state => {
          state.selectedFacebookPage = null;
          state.selectedInstagramPage = null;
          state.hasInstagramPagesAvailable = false;
        });

        throw error;
      } finally {
        set(state => { state.isConnecting = false });
        return null;
      }
    },

    setFacebookAdAccount: async (adAccount: AdAccountPageType | null): Promise<CurrentBrand | null> => {
      const { currentBrand, setCurrentBrand } = useBrand.getState() as BrandExistsState;

      try {
        if (!adAccount) {
          set((state) => {
            state.selectedFacebookAdAccount = null
          });
          return null;
        }

        set((state) => {
          state.isConnecting = true;
          state.selectedFacebookAdAccount = adAccount;
        });

        // Connect ad account to brand
        const updatedBrand = await connectToBrand_api({
          brandId: currentBrand?.id,
          brandSlug: currentBrand?.slug,
          service: 'facebook_ad_account',
          value: adAccount
        });

        // Update current brand with new ad account
        setCurrentBrand(updatedBrand);
        return updatedBrand;
      } catch (error) {
        console.error('Error connecting Facebook ad account:', error);

        // Reset state on error
        set((state) => {
          state.selectedFacebookAdAccount = null
        });

        throw error;
      } finally {
        set((state) => {
          state.isConnecting = false
        });

        return null
      }
    },

    setInstagramPage: (page: InstagramPageType | null) => set((state) => { state.selectedInstagramPage = page }),

    igIsBusinessAccount: () => {
      const { selectedInstagramPage } = get();
      if (!selectedInstagramPage) return false;
      return Boolean(selectedInstagramPage.businessAccount);
    },
    /** 
     * Initializes Facebook connection by validating existing token or clearing state
     * @param brand - Optional current brand to initialize Facebook for
     * @returns Promise<boolean> - True if successfully initialized with valid token, false otherwise
     */
    initializeFB: async (brand?: CurrentBrand) => {
      const { removeConnections, checkIfReadyToRunAds, loadPaymentDetails } = get();

      try {
        const { currentBrand } = useBrand.getState() as BrandExistsState
        let mainBrand = brand || currentBrand;
        const fbUser = mainBrand?.connections?.logged_in_fb_user;

        removeConnections(false)
        set((state) => {
          state.permissionsError = 'PERMISSIONS_ERROR';
          state.selectedFacebookPixel = null;
          state.selectedFacebookAdAccount = null;
          state.selectedFacebookPage = null;
          state.selectedInstagramPage = null;
        });
        // First try to validate existing token if we have one
        if (fbUser?.access_token) {
          const isValid = await isValidAccessToken_api(fbUser.access_token);
          if (isValid) {
            let refreshedFbUser = await getRefreshedFbUser(fbUser);
            // Set the token in our centralized service
            FacebookService.getInstance().setAccessToken(refreshedFbUser.access_token);

            let facebookPage = mainBrand?.connections?.facebook_page;
            let instagramPage = mainBrand?.connections?.instagram_page;
            let facebookAdAccount = mainBrand?.connections?.facebook_ad_account;
            let facebookPixel = mainBrand?.connections?.facebook_pixel;

            // Refresh images if needed
            const { fbPage: updatedFbPage, igPage: updatedIgPage } = await refreshSocialImages(
              facebookPage,
              instagramPage,
              refreshedFbUser?.access_token
            )

            // load ad account payment details 
            loadPaymentDetails(facebookAdAccount?.id)
            set((state) => {
              state.loggedInFacebookUser = refreshedFbUser;
              state.selectedFacebookPage = updatedFbPage;
              state.selectedInstagramPage = updatedIgPage;
              state.selectedFacebookAdAccount = facebookAdAccount;
              state.selectedFacebookPixel = facebookPixel;
              state.permissionsError = null;
            });
            return true;
          }
        }

        set((state) => {
          state.loggedInFacebookUser = null;
          state.permissionsError = 'PERMISSIONS_ERROR';
        });
        return false;

      } catch (error) {
        console.error('Error checking login status:', error);
        set((state) => {
          state.permissionsError = 'PERMISSIONS_ERROR';
        });
        return false;
      } finally {
        checkIfReadyToRunAds();
      }
    },

    loginWithFacebook: async (): Promise<LoggedUser | null> => {
      try {
        set((state) => {
          state.isLoggingIntoFacebook = true
        });

        const { currentBrand } = useBrand.getState() as BrandExistsState

        const authResponse = await loginWithFB();
        if (authResponse?.accessToken) {
          let userAccessToken = authResponse.accessToken;

          await connectUserToFacebook({
            brandId: currentBrand?.id,
            brandSlug: currentBrand?.slug,
            accessToken: userAccessToken
          })

          let profileData = await loggedInFb_api();

          get().getFacebookPages();
          set((state) => {
            state.loggedInFacebookUser = profileData;
            state.permissionsError = null;
          });


          return profileData;
        } else {
          throw new Error('Failed to get Facebook access token');
        }
      } catch (error: any) {
        console.error('Error logging in to Facebook:', error);
        set((state) => {
          state.isLoggingIntoFacebook = false;
          // Only set permissions error if it's not a user cancellation
          state.permissionsError = error.code === 'FB_AUTH_CANCELLED' ?
            null
            :
            'PERMISSIONS_ERROR' as keyof typeof ERROR_STATES;
        });
        return null;
      } finally {
        set((state) => {
          state.isLoggingIntoFacebook = false;
        });
      }
    },

    /** Requests additional Facebook permissions if they were previously denied */
    reRequestPermissions: async (permissions?: string) => {
      try {
        set(state => { state.isCheckingPermissions = true });

        const fb = FB();
        if (!fb) {
          throw new Error('Facebook SDK not initialized');
        }

        let permissionsList = permissions || 'pages_show_list,pages_read_engagement,instagram_basic,instagram_manage_insights,ads_management,ads_read'
        // Request new permissions
        const authResponse = await loginWithFB(
          'rerequest',
          permissionsList
        );

        if (!authResponse?.accessToken) {
          throw new Error('Failed to get Facebook access token');
        }

        set(state => {
          state.permissionsError = null;
        });

        await get().getFacebookPages();

      } catch (error: any) {
        console.error('Error requesting Facebook permissions:', error);

        // Determine error type based on Facebook error codes
        const errorType = error.code === 'FB_AUTH_CANCELLED' ? null
          : (error.code === 2500 || error.type === 'OAuthException') ? 'PERMISSIONS_ERROR'
            : 'PERMISSIONS_ERROR';

        set(state => {
          state.permissionsError = errorType;
        });

      } finally {
        set(state => { state.isCheckingPermissions = false });
      }
    },

    getFacebookPages: async (includeInstagram?: boolean): Promise<FacebookPageType[] | []> => {
      const state = get();
      const { currentBrand } = useBrand.getState() as BrandExistsState;
      if (state.isLoadingFacebookPages) return [];

      try {
        set(state => { state.isLoadingFacebookPages = true });

        let fields = 'name,category,id,access_token,followers_count,picture{url},is_published'
        if (includeInstagram) {
          fields += ',instagram_business_account{username,id,followers_count,profile_picture_url},connected_instagram_account{username,id,followers_count,profile_picture_url}'
        }
        const {
          data: pages
        } = await getFacebookPages_api(currentBrand?.id, fields);

        if (!pages || !Array.isArray(pages)) {
          throw new Error('No pages data returned');
        }

        if (pages.length === 0) {
          const missingPermissions = await checkForFBPermissions([
            'pages_show_list',
            'pages_read_engagement',
            'instagram_basic',
            'instagram_manage_insights',
            'ads_management',
            'ads_read'
          ]);

          if (Array.isArray(missingPermissions) && missingPermissions.length > 0) {
            set(state => {
              state.permissionsError = 'PERMISSIONS_ERROR';
              state.loggedInFacebookUser = null;
            });
          } else {
            set(state => {
              state.permissionsError = 'NO_PAGES_ERROR';
              state.loggedInFacebookUser = null;
            });
          }

          return []
        }

        const sortedPages = pages
          .sort((a: any, b: any) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
            if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
            return 0;
          })
          .sort((a: any, b: any) => {
            if (a.is_published === b.is_published) return 0;
            return a.is_published ? -1 : 1;
          });

        set(state => {
          state.facebookPages = sortedPages;
        });

        return sortedPages;
      } catch (error) {
        console.error('Error loading Facebook pages:', error);
        set(state => {
          state.permissionsError = 'PERMISSIONS_ERROR';
          state.loggedInFacebookUser = null;
        });
        return [];
      } finally {
        set(state => { state.isLoadingFacebookPages = false });
      }
    },

    // ... existing code ...

    /** Fetches and formats Facebook ad accounts, with optional business manager filtering */
    getFacebookAdAccounts: async (businessManagerId?: string): Promise<AdAccountPageType[]> => {
      const state = get();
      if (state.isLoadingFacebookAdAccounts) return [];
      try {
        set(state => { state.isLoadingFacebookAdAccounts = true });

        const results = await queryFB('/me/adaccounts', {
          params: {
            fields: 'amount_spent,name,id,account_status,user_tasks,currency,business{name,id}',
            limit: 250,
          }
        });

        const accounts = results.data.map((adAccount: any) => {
          const { user_tasks } = adAccount;
          let userHasCorrectAccess = false;

          if (user_tasks) {
            try {
              const hasAnalyze = user_tasks.indexOf('ANALYZE') > -1;
              const hasAdvertise = user_tasks.indexOf('ADVERTISE') > -1;
              userHasCorrectAccess = hasAnalyze && hasAdvertise;
            } catch (e) {
              console.error('e', e);
            }
          }

          return {
            ...adAccount,
            amount_spent: parseInt(adAccount.amount_spent) / 100,
            userHasAccess: userHasCorrectAccess
          };
        })
          .sort((a: any, b: any) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
            if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
            return 0;
          })
          .sort((a: any, b: any) => {
            const accountAccessA = a.userHasAccess;
            const accountAccessB = b.userHasAccess;

            if (accountAccessA && accountAccessB) return 0;
            if (accountAccessB && !accountAccessA) return 1;
            if (!accountAccessB && accountAccessA) return -1;
            return 0;
          })
          .sort((a: any, b: any) => {
            const accountStatusA = (FB_AD_ACCOUNT_STATUS)[a.account_status].valid;
            const accountStatusB = (FB_AD_ACCOUNT_STATUS)[b.account_status].valid;

            if (accountStatusA && accountStatusB || !accountStatusA && !accountStatusB) return 0;
            if (accountStatusB && !accountStatusA) return 1;
            if (!accountStatusB && accountStatusA) return -1;
            return 0;
          });

        if (!accounts || !accounts.length) {
          set(state => {
            state.permissionsError = null;
          });
          return [];
        }

        set(state => {
          state.permissionsError = null;
        });

        return accounts;

      } catch (error) {
        Logger.error('Error loading Facebook ad accounts:', error);
        set(state => {
          state.permissionsError = 'PERMISSIONS_ERROR';
        });
        return [];
      } finally {
        set(state => { state.isLoadingFacebookAdAccounts = false });
      }
    },

    getFacebookAuthToken: async () => {
      try {
        let loggedInUser = get().loggedInFacebookUser;
        if (loggedInUser?.access_token) {
          return loggedInUser.access_token;
        }

        const authResp = FB()?.getAuthResponse();
        if (authResp && authResp.accessToken) {
          return authResp.accessToken;
        }

        // If no auth response, try to login
        let profileData = await get().loginWithFacebook();
        if (profileData) {
          return profileData.access_token;
        }
        return null;
      } catch (error) {
        console.error('Error getting Facebook auth token:', error);
        return null;
      }
    },

    shouldShowLoginButton: () => {
      const { loggedInFacebookUser: loggedInUser } = get();

      return !loggedInUser;
    },

    renderFacebookLoginButton: (): JSX.Element | null => {
      const {
        loginWithFacebook: login,
        isLoggingIntoFacebook: isLoggingIn,
      } = get();

      const sty: Record<string, CSSProperties> = {
        container: {
          width: '100%'
        },
        button: {
          width: '100%',
          marginTop: 16
        },
        disclaimer: {
          fontSize: 14,
          color: Colors.greyDark,
          marginTop: 8,
          textAlign: 'left'
        }
      }

      return (
        <div
          className="facebookLoginContainer"
          style={sty.container}
        >
          <ButtonSymphony
            className="facebookLoginButton"
            style={sty.button}
            onClick={login}
            isLoading={isLoggingIn}
            disabled={isLoggingIn}
          >
            Login with Facebook
          </ButtonSymphony>
          <div
            className="facebookLoginDisclaimer"
            style={sty.disclaimer}
          >
            If you&apos;re using a popup blocker or Safari, you may need to disable it to allow for Facebook Login to popup.
          </div>
        </div>
      );
    },

    renderFacebookErrorMessage: () => {
      const { permissionsError, reRequestPermissions } = get();

      if (!permissionsError) {
        return null;
      }

      const sty: Record<string, CSSProperties> = {
        container: {
          padding: 16,
          textAlign: 'center'
        },
        reRequestButton: {
          width: '100%',
          marginTop: 16,
          color: Colors.purple,
          fontWeight: 'bold',
          padding: 16,
          borderTop: `1px solid ${Colors.greyLight}`,
          borderBottom: `1px solid ${Colors.greyLight}`,
          textAlign: 'center'
        },
        createPageLink: {
          display: 'block',
          width: '100%',
          marginTop: 16,
          color: Colors.purple,
          fontWeight: 'bold',
          padding: 16,
          borderTop: `1px solid ${Colors.greyLight}`,
          borderBottom: `1px solid ${Colors.greyLight}`,
          textAlign: 'center',
          textDecoration: 'none'
        }
      };

      return (
        <div className="socialConnectListError" style={sty.container}>
          <p>{ERROR_STATES[permissionsError].message}</p>
          {permissionsError === 'PERMISSIONS_ERROR' && (
            <ButtonSymphony
              className="reRequestPermissionsButton"
              style={sty.reRequestButton}
              onClick={reRequestPermissions}
            >
              Add Facebook Pages Permissions &gt;
            </ButtonSymphony>
          )}
          {permissionsError === 'NO_PAGES_ERROR' && (
            <a
              className="createPageLink"
              style={sty.createPageLink}
              href="https://www.facebook.com/help/104002523024878/?helpref=uf_share"
              target="_blank"
              rel="noopener noreferrer"
            >
              Create a New Page &gt;
            </a>
          )}
        </div>
      );
    },

    removeConnections: async (removeLoggedInUser?: boolean) => {
      set((state) => {
        if (removeLoggedInUser) {
          state.loggedInFacebookUser = null;
        }
        state.selectedFacebookPage = null;
        state.selectedInstagramPage = null;
        state.facebookPages = [];
        state.permissionsError = null;
      });
    },
    logoutFacebook: async () => {
      try {
        const { removeConnections } = get();
        const { currentBrand } = useBrand.getState() as BrandExistsState
        await disconnectAccounts_api(currentBrand?.slug, currentBrand?.id, currentBrand?.name);
        // await new Promise<void>((resolve) => {
        //   FB()?.logout(() => {
        //     resolve();
        //   });
        // });
        // Reset relevant state
        removeConnections(true)
      } catch (error) {
        console.error('Error logging out of Facebook:', error);
      }
    },

    setAdsTermsOfServiceStatus: (status: PreConnectionConfigs | undefined) => {
      set((state) => {
        state.adsTermsOfServiceStatus = status;
      })
    },

    checkIfReadyToRunAds: async (args?: {
      facebookAdAccount?: AdAccountPageType | null,
      requiresIgConnection?: boolean,
      expressSetupOrCustomPages?: 'custom' | 'express'
    }): Promise<boolean> => {
      const { requiresIgConnection, expressSetupOrCustomPages } = args || {};
      const { selectedFacebookPage, selectedInstagramPage, selectedFacebookAdAccount } = get();

      try {

        // Set loading state
        set((state) => {
          state.validatingConnections = true;
          state.canRunAds = false; // Reset while checking
        });

        // Express setup bypass
        if (expressSetupOrCustomPages === 'express') {
          set((state) => {
            state.canRunAds = true;
          });
          return true;
        }

        // Early return if no ad account
        if (!selectedFacebookAdAccount?.id) {
          set((state) => {
            state.canRunAds = false;
          });
          return false;
        }

        // Check all requirements
        // TODO MEGH: RE-add terms checking upfront
        // const hasAcceptedTerms = await get().hasUserAcceptedAdsTerms(selectedFacebookAdAccount);

        // Validate all requirements
        const requirements = {
          hasAdAccount: Boolean(selectedFacebookAdAccount?.id),
          hasAcceptedTerms: true,
          hasFacebookPage: Boolean(selectedFacebookPage),
          hasRequiredInstagram: requiresIgConnection ? Boolean(selectedInstagramPage) : true
        };

        const canRunAds = Object.values(requirements).every(Boolean);

        set((state) => {
          state.canRunAds = canRunAds;
        });

        return canRunAds;
      } catch (error) {
        Logger.error('Error validating connections:', error);
        set((state) => {
          state.canRunAds = false;
        });
        return false;
      } finally {
        set((state) => {
          state.validatingConnections = false;
        });
      }
    },


    /** 
     * Loads and sets payment details for a Facebook ad account
     * Returns success status and whether valid payment method exists
     */
    loadPaymentDetails: async (fbAdAccountId: string): Promise<PaymentMethodType | null> => {
      const { loggedInFacebookUser: loggedInUser } = get();
      if (!loggedInUser?.access_token) {
        return null;
      }

      const accessToken = loggedInUser?.access_token;

      set((state) => { state.isLoadingPaymentMethod = true });

      try {
        let results;
        try {
          results = await queryFB(`/${fbAdAccountId}`, {
            params: {
              fields: 'funding_source,funding_source_details,capabilities,spend_cap,currency',
              access_token: accessToken,
            },
          });
        } catch (e) {
          results = await queryFB(`/${fbAdAccountId}`, {
            params: {
              fields: 'funding_source,capabilities,spend_cap,currency',
              access_token: accessToken,
            },
          });
        }

        const hasValidPaymentMethods =
          results.capabilities &&
          results.capabilities.indexOf('HAS_VALID_PAYMENT_METHODS') > -1;

        let adAccountPaymentMethod: PaymentMethodType | null = {
          display_string: '',
          id: '',
          type: '',
          spendCap: 0,
          balance: 0,
          currency: DEFAULT_CURRENCY
        };

        if (hasValidPaymentMethods && results?.funding_source) {
          const info = results.funding_source_details || {};
          const type = info.type || 0;

          let display_string = '';
          switch (type) {
            case PAYPAL_BILLING_AGREEMENT:
              display_string = info.display_string;
              break;
            case CREDIT_CARD:
              display_string = info.display_string ? info.display_string : 'Credit Card';
              break;
            default:
              display_string = 'Connected';
              break;
          }

          adAccountPaymentMethod = {
            display_string,
            id: info.id,
            type: PAYMENT_METHODS[type],
          };
        }

        // Handle spend cap
        if (results?.spend_cap) {
          const cap = parseInt(results.spend_cap);
          if (cap > 0) {
            adAccountPaymentMethod.spendCap = cap;
          }
        }

        // Handle currency and minimum budget
        if (results?.currency && results.currency !== DEFAULT_CURRENCY) {
          const minimalAmount = await getMinimalAmount(results.currency);
          if (minimalAmount) {
            adAccountPaymentMethod.currency = results.currency;
            adAccountPaymentMethod.minimumCurrencyBudgetPerDay = Number(minimalAmount.toFixed(2))
          }
        } else {
          adAccountPaymentMethod.currency = DEFAULT_CURRENCY;
          adAccountPaymentMethod.minimumCurrencyBudgetPerDay = MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS
        }

        set((state) => {
          state.isLoadingPaymentMethod = false;
          state.adAccountPaymentMethod = adAccountPaymentMethod;
        });
        return adAccountPaymentMethod;

      } catch (error) {
        set((state) => { state.isLoadingPaymentMethod = false });
        return null;
      }
    },


    /** Gets the detailed status of various Facebook terms of service acceptances */
    getTermsAcceptanceStatus: async (adAccount: AdAccountPageType): Promise<PreConnectionStatus | null> => {
      const { loggedInFacebookUser: loggedInUser } = get();

      if (!loggedInUser?.access_token || !adAccount?.id) {
        return null;
      }

      try {
        // Get FB account details and our backend TOS status
        const adAccountDetails = await getAdAccountDetails(adAccount, loggedInUser.access_token);
        if (!adAccountDetails) {
          Logger.warn('No ad account details found');
          return null;
        }

        // Get TOS configurations and account status
        const tosConfigs = getTosStatus(adAccount, adAccountDetails);
        const accountStatus = getAccountStatus(adAccount, adAccountDetails);

        // If account is not valid, override TOS status
        const isValid = tosConfigs.status && accountStatus.status;

        // Return normalized status object
        const status = {
          allTermsAccepted: isValid,
          customAudienceAccepted: Boolean(tosConfigs.custom_audience_accepted.status),
          valueBasedAudienceAccepted: Boolean(tosConfigs.value_based_custom_audience_tos.status),
          userTosAccepted: Boolean(tosConfigs.user_tos_accepted.status)
        };

        // Update store with latest status
        set((state) => {
          state.adsTermsOfServiceStatus = tosConfigs;
          state.termsOfServiceAccepted = Boolean(status?.allTermsAccepted);
        });

        return status;
      } catch (error) {
        Logger.error('Error checking TOS acceptance status:', error);
        return null;
      }
    },

    /** Updates the terms of service status in the store */
    updateTermsOfServiceStatus: (configurations: PreConnectionConfigs) => {
      set({ adsTermsOfServiceStatus: configurations });
    },

    hasUserAcceptedAdsTerms: async (adAccount: AdAccountPageType): Promise<boolean> => {
      try {
        const status = await get().getTermsAcceptanceStatus(adAccount);
        set((state) => {
          state.termsOfServiceAccepted = Boolean(status?.allTermsAccepted);
        });
        return Boolean(status?.allTermsAccepted);
      } catch (error) {
        Logger.error('Error checking if user has accepted terms:', error);
        return false;
      }
    }
  }))
);

export default useFacebookConnections; 